// frontend/src/features/livestock/CompareSelectBar.tsx
// Select mode on Tank Shots (design 7.1): a bar fixed to the foot of the page,
// "Pick 2 shots · 1 selected", Cancel and Compare (two picks, or none for
// First vs Latest). On the app layer, under modals, the header and the mobile
// menu. While it shows, html[data-compare-select] hides the floating Log data
// button, --rdh-bottom-bar-h lifts the upload strip above it, and a spacer of
// its height lets the last row of the grid scroll clear of it.

import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Group, Text } from '@mantine/core';
import { IconLayoutColumns } from '@tabler/icons-react';
import classes from './Compare.module.css';

export interface CompareSelectBarProps {
  selected: number;
  isMobile: boolean;
  onCompare: () => void;
  onCancel: () => void;
}

export function CompareSelectBar({
  selected,
  isMobile,
  onCompare,
  onCancel,
}: CompareSelectBarProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const bar = ref.current;
    if (!bar) return undefined;
    const root = document.documentElement;
    const update = (): void => {
      const next = bar.offsetHeight;
      root.style.setProperty('--rdh-bottom-bar-h', `${next}px`);
      setHeight(next);
    };
    update();
    root.setAttribute('data-compare-select', '');
    // Without ResizeObserver (older browsers) the height measured once stands.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--rdh-bottom-bar-h');
      root.removeAttribute('data-compare-select');
    };
  }, []);

  const controlHeight = isMobile ? 44 : 36;
  return (
    <>
      <Box h={height} aria-hidden />
      <Box ref={ref} className={classes.selectBar} role="region" aria-label="Pick shots to compare">
        <Group justify="space-between" gap="xs" wrap="nowrap" maw={1320} mx="auto">
          <Text size="sm" fw={500} className={classes.tabular} aria-live="polite">
            <span className={classes.nowrap}>Pick 2 shots</span> ·{' '}
            <span className={classes.nowrap}>{selected} selected</span>
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Button variant="subtle" color="gray" h={controlHeight} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              h={controlHeight}
              leftSection={<IconLayoutColumns size={16} />}
              disabled={selected === 1}
              onClick={onCompare}
            >
              Compare
            </Button>
          </Group>
        </Group>
      </Box>
    </>
  );
}
